<script setup lang="ts">
import type { LucideIcon } from '@lucide/vue'
import {
    Activity,
    Bot,
    Brain,
    BriefcaseBusiness,
    Building,
    Building2,
    ChevronRight,
    CirclePlay,
    ClipboardList,
    CreditCard,
    Gauge,
    Globe,
    Hourglass,
    IdCard,
    Inbox,
    KeyRound,
    LibraryBig,
    ListTodo,
    ListTree,
    MessageCircle,
    Network,
    Orbit,
    Plug,
    Scale,
    Settings,
    ShieldCheck,
    Sparkles,
    TriangleAlert,
    Unplug,
    UserRound,
    UsersRound,
    Wallet,
    Webhook,
    Workflow,
    Wrench,
    Zap,
} from '@lucide/vue'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api, type EnterpriseAttention } from '@/api'
import NavCounts, { type NavCount } from '@/components/NavCounts.vue'
import { useLiveResource } from '@/composables/useLiveResource'
import { useUiMode } from '@/composables/useUiMode'
import { blockerAlertTotal } from '@/lib/blockers'
import { kpiBoardLights } from '@/lib/kpi'
import { grantAllowsScreen, grantCovers, type NavGrant, type PortalResource } from '@/lib/navGrant'
import { type RunStatusTone, statusTone } from '@/lib/statusTone'

/*
 * BG §18.4 — an alert count is a status too, so it is coloured by what it *is* rather than by the
 * fact that it is an alert. Every badge in this nav used to be magenta, which said "something is
 * wrong" about a queue of approvals waiting politely for you.
 *
 * Count, tone and label travel together and are all required: a badge that knows its number but not
 * what the number means is exactly the badge this replaced. The tone is what the count means; whether
 * the nav shows it is the page's call (`alertTone`, N5 of #1697).
 */
type NavBadge = { count: number; tone: RunStatusTone; label: string }

/** One of today's pages, linked at the path it has always had and under the title it has always had. */
type NavPage = {
    title: string
    to: string
    icon: LucideIcon
    exact?: boolean
    badge?: NavBadge
    /** Enterprise's KPI lights: how many KPIs are off target, at risk and on target, each in its own colour. */
    lights?: NavBadge[]
    /**
     * What its counts count, when not the page's own title: `workflows` on Workflows. Its count labels and its
     * badge on a closed place say this word.
     */
    noun?: string
    /** Work that cannot go on until someone looks: blocked. */
    blocked?: number
    /** Work waiting on a person's decision, at a gate. */
    deciding?: number
    running?: number
    queued?: number
    /** Work waiting on anything else: another run, a cap, a team switched off. */
    waiting?: number
    /**
     * N5 of #1697 (#1745): this page's counts keep the colour of what they mean. The pages that ask for you set it
     * (Gates, Plans, Self-Improvement, Blockers, Waiting), wherever they sit, and so do the pages that count work in
     * flight (Workflows, Executions, Runs): blocked pink, waiting on a decision blue, running amber, queued and other
     * waiting grey. Enterprise sets it too, for its KPI lights. Every other count, and any page added later, is neutral
     * by default.
     */
    alertTone?: true
}

/**
 * #1744: one of the founder's places. Chat and Catalog are a page of their own and link straight to it. Team, Work,
 * Settings and Advanced each hold several pages and open to list them. A merged page joins a place as one more page
 * (Work's Workflows, #1747), and every page keeps its path.
 */
type NavPlace =
    | { kind: 'link'; title: string; page: NavPage }
    | {
          kind: 'group'
          title: string
          icon: LucideIcon
          pages: NavPage[]
          /** Shown only in Advanced mode. Its pages still open by URL in Standard: the router never asks the mode. */
          advancedOnly?: boolean
      }

type NavGroup = Extract<NavPlace, { kind: 'group' }>

/** Nothing to report is no badge at all, so the template never renders a zero. */
function navBadge(count: number, tone: RunStatusTone, label: string): NavBadge | undefined {
    return count > 0 ? { count, tone, label } : undefined
}

/**
 * #622: the signed-in person's grant, the same `isOwner` / `scopes` vocabulary `GET /auth/me` carries
 * (`req.os`, session.guard.ts) and `UsersView.vue`'s scope chips already show. Every destination below is
 * offered only where the grant covers the resource behind it, so somebody invited with a narrow grant sees
 * a portal that fits them rather than a nav full of refusals. An owner holds everything, with nothing
 * enumerated to check — exactly the convention the guard uses.
 */
const props = defineProps<{
    isOwner?: boolean
    scopes?: string[] | null
    platformAdmin?: boolean
    /** The person's own mail address here, when an owner has given them one: what offers them Inbox. */
    mailAddress?: string | null
}>()

const grant = computed<NavGrant>(() => ({
    isOwner: props.isOwner,
    scopes: props.scopes,
    platformAdmin: props.platformAdmin,
}))

/** Whether this person may use a destination at all — the one question the nav asks about each item. */
function offers(to: string): boolean {
    return grantAllowsScreen(grant.value, to)
}

const route = useRoute()
const { isMobile, setOpenMobile } = useSidebar()

/*
 * No Standard/Advanced switch here: it is a setting on the Account page, saved on the person (#1949). The mode
 * changes words, not access: every page opens by URL in either mode, because the router's guard asks the grant and
 * onboarding, never the mode.
 *
 * #1744: the nav follows that shared mode, live. Advanced adds the Advanced place after Settings; the five places are
 * the same in both modes. The nav only reads the mode here; it never sets it.
 */
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')

const alerts = ref({
    chat: 0,
    inbox: 0,
    gates: 0,
    blockers: 0,
    waiting: 0,
    runs: 0,
    spend: 0,
    selfImprovement: 0,
    plans: 0,
    kpis: { green: 0, amber: 0, red: 0 },
})
const { data: activity } = useLiveResource(
    () => 'sidebar-activity',
    // Workflows and Executions share these totals, so they are read only for a grant that covers executions:
    // asking for them otherwise is a refusal every second on behalf of a nav item this person is not offered.
    signal => (grantCovers(grant.value, 'executions') ? api.executionCounts(signal) : Promise.resolve(null)),
)
let timer: ReturnType<typeof setInterval> | null = null

/**
 * The spend warning moves with the day's spend, not second by second, and building it costs several
 * queries — so it is read once a minute rather than with the 5s counts. `null` means read on the next tick.
 */
const SPEND_EVERY_MS = 60_000
let spendReadAt: number | null = null

/**
 * The KPI lights move when a reading is recorded, which is daily or weekly, so they too are read once a minute. They
 * badge Enterprise under Advanced only, so they are not read at all while Advanced is hidden.
 */
const KPIS_EVERY_MS = 60_000
let kpisReadAt: number | null = null

function tabVisible(): boolean {
    return document.visibilityState !== 'hidden'
}

/** Nobody is looking at a hidden tab's badges, so it stops asking; coming back refreshes at once. */
function visibilityChanged(): void {
    if (tabVisible()) {
        void loadAlerts()
    }
}

onMounted(() => {
    void loadAlerts()
    timer = setInterval(() => {
        if (tabVisible()) {
            void loadAlerts()
        }
    }, 5000)
    document.addEventListener('visibilitychange', visibilityChanged)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
    document.removeEventListener('visibilitychange', visibilityChanged)
})

watch(
    () => route.fullPath,
    () => {
        if (isMobile.value) {
            setOpenMobile(false)
        }
    },
)

/**
 * One resource's alert count (#622). Never asked for at all where the grant does not cover the resource —
 * counting what somebody may not see is a refusal per poll and a badge nobody can act on — and guarded on
 * its own, so one resource's failure cannot take another resource's badge down with it. `null` means
 * "nothing new to say", and the last known count stands.
 */
async function alertFor<T>(resource: PortalResource, read: () => Promise<T>): Promise<T | null> {
    if (!grantCovers(grant.value, resource)) {
        return null
    }
    try {
        return await read()
    } catch {
        return null
    }
}

/**
 * #678: Chat, Gates, Plans and Self-Improvement count what waits on this person here, by the one rule that decides
 * who needs to know about an item (the API's attention audience): threads they own with unread replies, gates waiting
 * on the CEO (never a lead's), plans awaiting the CEO and pending recommendations, an owner's or raised by a run they
 * asked for, and Inbox's unread mail once the person has an address. One read serves the five, the very row the
 * enterprise picker shows for this enterprise, asked only once a badge it feeds is offered; each badge still asks its own resource's grant (`alertFor`).
 */
function attentionRead(): () => Promise<EnterpriseAttention> {
    let read: Promise<EnterpriseAttention> | null = null
    return () => {
        read ??= api.currentEnterpriseAttention()
        return read
    }
}

/**
 * The person's own unread mail, for the Inbox badge: counted only once they have an address, and never under a grant,
 * since their own mail is theirs (`isOwnMailRoute`). The attention row carries it (`mail`), the very count the
 * enterprise picker shows for this enterprise; an API older than that sends none, and then their own mail route's
 * total serves, as before. Guarded on its own like every other count: a failure keeps the last known number and
 * takes no other badge down.
 */
async function inboxUnread(attention: () => Promise<EnterpriseAttention>): Promise<number | null> {
    if (!props.mailAddress) {
        return null
    }
    try {
        const row = await attention().catch(() => null)
        return typeof row?.mail === 'number' ? row.mail : await api.myMailUnreadCount()
    } catch {
        return null
    }
}

async function loadAlerts(): Promise<void> {
    const attention = attentionRead()
    const [chat, inbox, gates, blockers, waiting, recommendations, plans] = await Promise.all([
        alertFor('chats', async () => (await attention()).chats),
        inboxUnread(attention),
        alertFor('gates', async () => (await attention()).gates),
        alertFor('blockers', () => api.blockers()),
        // #1457: the Waiting list's own `total`, counted off the same rows the page lists, so the two agree. Asked
        // under `runs`, the resource the API serves it under (#1678), so a grant without it never polls a refusal.
        alertFor('runs', async () => (await api.waiting()).total),
        alertFor('recommendations', async () => (await attention()).recommendations),
        // #234: a plan parked on a CEO decision (`awaiting_ceo`, docs/plans.md) is otherwise invisible outside the
        // chat thread that raised it, so the badge counts it.
        alertFor('plans', async () => (await attention()).plans),
    ])
    const last = alerts.value
    alerts.value = {
        chat: chat ?? last.chat,
        // No address means no inbox and nothing to count; a failed read keeps the last count, as the others do.
        inbox: props.mailAddress ? (inbox ?? last.inbox) : 0,
        gates: gates ?? last.gates,
        // Blockers answers two badges — what is blocked or failed, and what is merely parked on a cap — so
        // one read serves both, and both keep their last known value together when it is the one that fails.
        blockers: blockers ? blockerAlertTotal(blockers) : last.blockers,
        runs: blockers ? blockers.paused.length : last.runs,
        waiting: waiting ?? last.waiting,
        // Spend is not read with the others: `loadSpendAlert` owns this badge, on its own once-a-minute
        // clock, so the last known warning stands between those reads.
        spend: last.spend,
        // Pending recommendations badge Self-Improvement only. Tasks carried the same number, which
        // read as a second, unrelated alert rather than the one decision queue it actually is.
        selfImprovement: recommendations ?? last.selfImprovement,
        plans: plans ?? last.plans,
        // `loadKpiLights` owns this badge on its own minute clock, like spend.
        kpis: last.kpis,
    }
    // Guarded like every other resource (#622) — a grant with no `spend` scope never asks for it, and a
    // refusal here cannot blank the badges above — but read on the minute clock rather than each poll.
    await alertFor('spend', () => loadSpendAlert(blockers?.spendEnabled !== false))
    // The board Enterprise draws is an `enterprise:read` route; guarded and clocked like spend.
    await alertFor('enterprise', loadKpiLights)
}

/** Enterprise's KPI lights, off the same board the page draws, so the nav and the page agree on every colour. */
async function loadKpiLights(): Promise<void> {
    if (!advancedMode.value) {
        return
    }
    if (kpisReadAt !== null && Date.now() - kpisReadAt < KPIS_EVERY_MS) {
        return
    }
    kpisReadAt = Date.now()
    alerts.value.kpis = kpiBoardLights(await api.kpiBoard())
}

/**
 * Spend switched off on the Spend page enforces no cap, so there is nothing to warn about and nothing is
 * read. The blockers payload already says which on every 5s poll, so switching it back on is picked up
 * there and read straight away.
 */
async function loadSpendAlert(enabled: boolean): Promise<void> {
    if (!enabled) {
        spendReadAt = null
        alerts.value.spend = 0
        return
    }
    if (spendReadAt !== null && Date.now() - spendReadAt < SPEND_EVERY_MS) {
        return
    }
    spendReadAt = Date.now()
    const spend = await api.spend()
    alerts.value.spend = spend.enabled !== false && spend.warning80 ? 1 : 0
}

function isActive(to: string, exact = false): boolean {
    if (exact) {
        return route.path === to
    }
    if (to === '/webhooks' && route.path.startsWith('/webhook/')) return true
    return route.path === to || route.path.startsWith(`${to}/`)
}

/** A page's own counts, in the order they always sat: running, queued and waiting work, then its alert badge. */
function progressCounts(page: NavPage): NavCount[] {
    const noun = page.noun ?? page.title.toLowerCase()
    const states: [keyof NavPage & ('blocked' | 'deciding' | 'running'), NavCount][] = [
        ['blocked', { key: 'blocked', status: 'blocked', count: 0, label: `blocked ${noun}` }],
        ['deciding', { key: 'deciding', status: 'waiting_on_gate', count: 0, label: `${noun} waiting on a decision` }],
        ['running', { key: 'running', status: 'running', count: 0, label: `running ${noun}` }],
    ]
    const counts = states.flatMap(([field, count]) => {
        const value = page[field]
        return value ? [{ ...count, count: value, label: `${value} ${count.label}` }] : []
    })
    // Queued work and a wait nobody is being asked to end are both grey, so they are one number, as on a closed place.
    const idle = (page.queued ?? 0) + (page.waiting ?? 0)
    if (idle > 0) {
        const queues = page.queued !== undefined
        const words = [queues ? 'queued' : null, page.waiting === undefined ? null : 'waiting']
        const label = `${idle} ${words.filter(Boolean).join(' or ')} ${noun}`
        counts.push(
            queues
                ? { key: 'idle', status: 'queued', count: idle, label }
                : { key: 'idle', status: 'waiting', tone: 'neutral', count: idle, label },
        )
    }
    return counts
}

/** The tone a page's badge is drawn in: its own where the page keeps its colours, neutral everywhere else (N5, #1745). */
function badgeTone(page: NavPage, badge: NavBadge): RunStatusTone {
    return page.alertTone ? badge.tone : 'neutral'
}

function pageCounts(page: NavPage): NavCount[] {
    const progress = progressCounts(page)
    const counts = page.alertTone ? progress : progress.map((count): NavCount => ({ ...count, tone: 'neutral' }))
    if (page.badge) {
        const { count, label } = page.badge
        counts.push({ key: 'badge', tone: badgeTone(page, page.badge), count, label: `${count} ${label}` })
    }
    for (const light of page.lights ?? []) {
        const { count, label } = light
        counts.push({ key: `light-${light.tone}`, tone: badgeTone(page, light), count, label: `${count} ${label}` })
    }
    return counts
}

/**
 * What a closed place names a page's badge by: what the page's counts count, where that is not its title. A closed
 * Work still says "Runs: 2 paused" (#1747, CTO decision 5): the paused things are runs, whichever page carries them.
 */
function countedAs(page: NavPage): string {
    return page.noun ? `${page.noun.charAt(0).toUpperCase()}${page.noun.slice(1)}` : page.title
}

/**
 * Which tone asks more of you (#1744, CTO decision 8): something failed or is blocked, then a decision waits on you,
 * then work is moving, then everything else. A closed place lists its pills in this order, most urgent first.
 */
const URGENCY: Record<RunStatusTone, number> = { failed: 4, action: 3, running: 2, neutral: 1, completed: 0 }

/** The tone a count is drawn in: its own, or its run state's. */
function drawnTone(count: NavCount): RunStatusTone {
    return count.tone ?? statusTone(count.status ?? '')
}

/**
 * What a closed place shows in place of the pages it hides (#1744): one pill per drawn tone, summing every count its
 * pages show in that tone, run states and badges alike, so the pills add up to what the open place shows. Each pill
 * is labelled with its parts. Tones are never summed together: blocked work, a decision waiting on you, running work
 * and the rest mean different things. Open, the place shows nothing of its own and each page carries its own counts.
 */
function placeCounts(place: NavGroup): NavCount[] {
    const byTone = new Map<RunStatusTone, { count: number; labels: string[] }>()
    for (const page of place.pages) {
        for (const count of pageCounts(page)) {
            const tone = drawnTone(count)
            const sum = byTone.get(tone) ?? { count: 0, labels: [] }
            byTone.set(tone, {
                count: sum.count + count.count,
                labels: [...sum.labels, `${countedAs(page)}: ${count.label}`],
            })
        }
    }
    return [...byTone.entries()]
        .sort(([a], [b]) => URGENCY[b] - URGENCY[a])
        .map(([tone, { count, labels }]) => ({ key: `tone-${tone}`, tone, count, label: labels.join(', ') }))
}

/**
 * Workflows' and Executions' counts: each open execution once, by what it is doing. A wait on a gate is a decision
 * someone owes (blue); any other wait is grey. `?? 0` reads an API older than the split as having none.
 */
function workflowCounts(): Pick<NavPage, 'blocked' | 'deciding' | 'running' | 'queued' | 'waiting'> {
    const counts = activity.value
    if (!counts) return {}
    const deciding = counts.waitingOnGate ?? 0
    return {
        blocked: counts.blocked ?? 0,
        deciding,
        running: counts.running,
        queued: counts.queued,
        waiting: counts.waiting - deciding,
    }
}

/** Runs' counts: each open run once, split the same way as `workflowCounts`. */
function runCounts(): Pick<NavPage, 'blocked' | 'deciding' | 'running' | 'waiting'> {
    const counts = activity.value
    if (!counts) return {}
    const deciding = counts.waitingRunsOnGate ?? 0
    return {
        blocked: counts.blockedRuns ?? 0,
        deciding,
        running: counts.runningRuns,
        waiting: counts.waitingRuns - deciding,
    }
}

/** Enterprise's KPI lights, worst first like every count here: off target pink, at risk amber, on target green. */
function kpiLights(): NavBadge[] {
    const { red, amber, green } = alerts.value.kpis
    return [
        navBadge(red, 'failed', red === 1 ? 'KPI off target' : 'KPIs off target'),
        navBadge(amber, 'running', amber === 1 ? 'KPI at risk' : 'KPIs at risk'),
        navBadge(green, 'completed', green === 1 ? 'KPI on target' : 'KPIs on target'),
    ].filter((badge): badge is NavBadge => badge !== undefined)
}

/**
 * Every place, every page it holds, in the order of the PRD's nav table (#1697 N1). Each destination sits in exactly
 * one place, none renamed: Chat and Catalog stand alone, the rest are grouped.
 */
const allPlaces = computed<NavPlace[]>(() => [
    {
        kind: 'link',
        title: 'Chat',
        page: {
            title: 'Chat',
            to: '/chat',
            icon: MessageCircle,
            /*
             * Replies from your members you have not opened yet: the member is waiting on you to read them, so the
             * count keeps its `action` blue like Gates. A message about a decision already made elsewhere is not one.
             */
            alertTone: true,
            badge: navBadge(alerts.value.chat, 'action', 'unread'),
        },
    },
    /*
     * The person's own mailbox, right under Chat: offered only once an owner has given them an address here, and
     * counting the mail they have not opened yet in the same `action` blue as Chat's unread replies, for the same
     * reason: somebody wrote to them and is waiting on them to read it.
     */
    ...(props.mailAddress
        ? [
              {
                  kind: 'link' as const,
                  title: 'Inbox',
                  page: {
                      title: 'Inbox',
                      to: '/inbox',
                      icon: Inbox,
                      alertTone: true as const,
                      badge: navBadge(alerts.value.inbox, 'action', 'unread'),
                  },
              },
          ]
        : []),
    {
        kind: 'group',
        title: 'Team',
        icon: Network,
        pages: [
            { title: 'Map', to: '/', icon: Orbit, exact: true },
            { title: 'Teams', to: '/teams', icon: UsersRound },
            /*
             * A teammate is one card on Members (#1746), and their job (the roles they hold) is edited on that card.
             * The Roles page itself moved to Advanced.
             */
            { title: 'Members', to: '/members', icon: UserRound },
            // Each teammate's work done, idle time and waiting time (#2123). Offered only where the grant reads runs.
            { title: 'Activity', to: '/activity', icon: Activity },
            /*
             * What the enterprise has learned, and the evidence behind it (#690). No badge: memory growing is
             * not something waiting on you, so nothing here would be counting anything a reader must act on.
             */
            { title: 'Memory', to: '/memory', icon: Brain },
        ],
    },
    {
        kind: 'group',
        title: 'Work',
        icon: BriefcaseBusiness,
        pages: [
            {
                // Approvals waiting on a person lead Work: the one queue every founder works through daily.
                title: 'Gates',
                to: '/gates',
                icon: ShieldCheck,
                alertTone: true,
                badge: navBadge(alerts.value.gates, 'action', 'waiting for your approval'),
            },
            {
                /*
                 * One list of every workflow (an execution), each opening into its runs (#1747, N8 of #1697). Its
                 * counts are the Executions page's, in their own colours, and it still counts the runs paused on a
                 * spend cap.
                 */
                title: 'Workflows',
                to: '/work',
                icon: ListTree,
                noun: 'workflows',
                alertTone: true,
                badge: navBadge(alerts.value.runs, 'neutral', 'runs paused'),
                ...workflowCounts(),
            },
            { title: 'Tasks', to: '/tasks', icon: ListTodo },
        ],
    },
    /*
     * What other people made — the clone catalog, and whatever else ends up being shared between enterprises. A
     * browsing destination of its own, linked straight to, rather than chrome in the header.
     */
    { kind: 'link', title: 'Catalog', page: { title: 'Catalog', to: '/catalog', icon: LibraryBig } },
    {
        kind: 'group',
        title: 'Settings',
        icon: Settings,
        pages: [
            { title: 'Agents', to: '/agents', icon: Bot },
            { title: 'Tools', to: '/tools', icon: Unplug },
            {
                title: 'Spend',
                to: '/spend',
                icon: Wallet,
                // Past 80% of the cap. Nothing has failed — it is a call on whether to raise it.
                badge: navBadge(alerts.value.spend, 'action', 'spend warning'),
            },
            /*
             * The plan, this period's usage, payment methods and invoices (os#1615): money the enterprise pays,
             * beside Spend, the money its agents spend, and before People.
             */
            { title: 'Billing', to: '/billing', icon: CreditCard },
            /*
             * Who can reach this enterprise and what they hold (#618) — distinct from Members, the AI org
             * chart, which stays in Team. People sits in Settings after Spend and Billing: it is an administrative
             * screen about access, the same kind of thing as caps, not a daily destination. The product calls
             * these humans people (#1264); the route, the `users` grant resource and the API keep their names.
             */
            { title: 'People', to: '/users', icon: UserRound },
            /*
             * The enterprise's own domain (#422): attaching one, the DNS to publish, and whether email moves to
             * it. An enterprise-wide setting served by `enterprise/*`.
             */
            { title: 'Domains', to: '/domains', icon: Globe },
        ],
    },
    {
        /*
         * The builder's pages (#1744, CTO decision 4): the enterprise itself and what can reach it from outside.
         * Hidden until Advanced mode is on, and reachable by URL whatever the mode.
         */
        kind: 'group',
        title: 'Advanced',
        icon: Wrench,
        advancedOnly: true,
        pages: [
            {
                title: 'Enterprise',
                to: '/enterprise',
                icon: Building,
                exact: true,
                // How the enterprise is doing at a glance: its KPIs by light, as the page's KPI board draws them.
                alertTone: true,
                lights: kpiLights(),
            },
            /*
             * Roles (#1746, CTO decision 8) sits right after Enterprise: both are about how the enterprise is
             * structured. A teammate's roles are edited on their card on Members; this is where roles themselves
             * are managed. `/roles`, `/roles/new` and `/roles/:id` still open by URL in Standard mode.
             */
            { title: 'Roles', to: '/roles', icon: IdCard },
            { title: 'Webhooks', to: '/webhooks', icon: Webhook },
            { title: 'API tokens', to: '/api-tokens', icon: KeyRound },
            /*
             * Connected apps (#333) sits directly after API tokens: both answer "what can reach this
             * enterprise, and how do I stop it", one for a machine credential and one for an external agent.
             */
            { title: 'Connected apps', to: '/connected-apps', icon: Plug },
            /*
             * The Executions board and the Runs list moved here whole from Work (#1747, CTO decision 2), as Roles did in
             * #1746: Work's Workflows replaces them there. Each keeps its own counts and badge, and
             * `/executions` and `/runs` still open by URL in Standard mode.
             */
            {
                title: 'Executions',
                to: '/executions',
                icon: Workflow,
                alertTone: true,
                ...workflowCounts(),
            },
            {
                title: 'Runs',
                to: '/runs',
                icon: CirclePlay,
                alertTone: true,
                // Runs parked on a spend cap: stopped, but nothing is being asked of you yet.
                badge: navBadge(alerts.value.runs, 'neutral', 'paused'),
                ...runCounts(),
            },
            /*
             * The rest of what waits on a person: decisions, what is blocked, and open work nothing is working on.
             * Each keeps its own alert tone here, and its path opens by URL in Standard mode.
             */
            {
                title: 'Plans',
                to: '/plans',
                icon: ClipboardList,
                alertTone: true,
                // A plan sitting in `awaiting_ceo`: a decision is being asked of you, same as a gate.
                badge: navBadge(alerts.value.plans, 'action', 'waiting for your decision'),
            },
            {
                title: 'Self-Improvement',
                to: '/self-improvement',
                icon: Sparkles,
                alertTone: true,
                badge: navBadge(alerts.value.selfImprovement, 'action', 'waiting for your decision'),
            },
            {
                title: 'Blockers',
                to: '/blockers',
                icon: TriangleAlert,
                alertTone: true,
                badge: navBadge(alerts.value.blockers, 'failed', 'blocked or failed'),
            },
            {
                // Open work nothing is working on (#1457). Nothing has failed and no decision is pending: its last
                // run did not finish it, so it waits until someone restarts it or marks it finished.
                title: 'Waiting',
                to: '/waiting',
                icon: Hourglass,
                alertTone: true,
                badge: navBadge(alerts.value.waiting, 'neutral', 'open with nothing working on it'),
            },
        ],
    },
])

/*
 * The Staff area (#2067): the platform's own settings, the same for every enterprise. It is not one of the
 * enterprise's places (#1744), so it sits in its own group below them, under a "Staff" heading, in either mode. Its
 * pages are offered only to a platform admin (`grantAllowsScreen`), so for everybody else the group has no pages
 * and is not drawn at all: no heading over an empty list. Enterprises and Reconciliation are #394's; Performance shows
 * run capacity across every enterprise.
 */
const allStaffPages: NavPage[] = [
    { title: 'Enterprises', to: '/staff/enterprises', icon: Building2 },
    { title: 'Reconciliation', to: '/staff/reconciliation', icon: Scale },
    { title: 'Performance', to: '/staff/performance', icon: Gauge },
    { title: 'Express tools', to: '/staff/express-tools', icon: Zap },
]

const staffPages = computed(() => allStaffPages.filter(page => offers(page.to)))

/**
 * The places this person is actually offered (#622, unchanged by #1744): each page only where the grant covers it,
 * then each place only where it still holds a page. A place with nothing in it for you is not shown at all, so a
 * narrow grant sees the few places that fit it; Team always stays, because its Map is open to everybody.
 */
const places = computed<NavPlace[]>(() =>
    allPlaces.value.flatMap((place): NavPlace[] => {
        if (place.kind === 'link') {
            return offers(place.page.to) ? [place] : []
        }
        if (place.advancedOnly && !advancedMode.value) {
            return []
        }
        const pages = place.pages.filter(page => offers(page.to))
        return pages.length > 0 ? [{ ...place, pages }] : []
    }),
)

function holdsCurrentPage(place: NavGroup): boolean {
    return place.pages.some(page => isActive(page.to, page.exact))
}

/**
 * Which places are open. The one holding the page on screen opens on load and whenever you navigate to one of its
 * pages; the rest start closed, and nothing is remembered between visits (#1744, CTO decision 7). Opening or closing
 * a place is only this state: it never navigates, so it never closes the mobile sidebar either.
 */
const openPlaces = ref(new Set<string>())

function isOpen(title: string): boolean {
    return openPlaces.value.has(title)
}

function setOpen(title: string, open: boolean): void {
    if (open) {
        openPlaces.value.add(title)
    } else {
        openPlaces.value.delete(title)
    }
}

/** The offered place holding the page on screen, if any: `/enterprise` in Standard mode is held by none. */
const currentPlace = computed(
    () => places.value.find(place => place.kind === 'group' && holdsCurrentPage(place))?.title,
)

watch(
    [() => route.path, currentPlace],
    () => {
        if (currentPlace.value) {
            setOpen(currentPlace.value, true)
        }
    },
    { immediate: true },
)
</script>

<template>
	<Sidebar collapsible="offcanvas">
		<SidebarContent>
			<SidebarGroup>
				<SidebarGroupContent>
					<SidebarMenu>
						<SidebarMenuItem v-for="place in places" :key="place.title">
							<SidebarMenuButton
								v-if="place.kind === 'link'"
								as-child
								:is-active="isActive(place.page.to, place.page.exact)"
								:tooltip="place.title"
							>
								<RouterLink :to="place.page.to">
									<component :is="place.page.icon" />
									<span class="min-w-0 flex-1 truncate">{{ place.title }}</span>
									<NavCounts :counts="pageCounts(place.page)" />
								</RouterLink>
							</SidebarMenuButton>
							<Collapsible
								v-else
								:open="isOpen(place.title)"
								@update:open="(open: boolean) => setOpen(place.title, open)"
							>
								<CollapsibleTrigger as-child>
									<SidebarMenuButton :is-active="!isOpen(place.title) && holdsCurrentPage(place)">
										<component :is="place.icon" />
										<span class="min-w-0 flex-1 truncate">{{ place.title }}</span>
										<NavCounts v-if="!isOpen(place.title)" :counts="placeCounts(place)" />
										<ChevronRight
											aria-hidden="true"
											class="shrink-0 transition-transform motion-reduce:transition-none"
											:class="{ 'rotate-90': isOpen(place.title) }"
										/>
									</SidebarMenuButton>
								</CollapsibleTrigger>
								<CollapsibleContent>
									<SidebarMenuSub>
										<SidebarMenuSubItem v-for="page in place.pages" :key="page.to">
											<SidebarMenuSubButton as-child :is-active="isActive(page.to, page.exact)">
												<RouterLink :to="page.to">
													<component :is="page.icon" />
													<span class="min-w-0 flex-1 truncate">{{ page.title }}</span>
													<NavCounts :counts="pageCounts(page)" />
												</RouterLink>
											</SidebarMenuSubButton>
										</SidebarMenuSubItem>
									</SidebarMenuSub>
								</CollapsibleContent>
							</Collapsible>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarGroupContent>
			</SidebarGroup>
			<SidebarGroup v-if="staffPages.length > 0">
				<SidebarGroupLabel>Staff</SidebarGroupLabel>
				<SidebarGroupContent>
					<SidebarMenu>
						<SidebarMenuItem v-for="page in staffPages" :key="page.to">
							<SidebarMenuButton as-child :is-active="isActive(page.to, page.exact)" :tooltip="page.title">
								<RouterLink :to="page.to">
									<component :is="page.icon" />
									<span class="min-w-0 flex-1 truncate">{{ page.title }}</span>
								</RouterLink>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarGroupContent>
			</SidebarGroup>
		</SidebarContent>
	</Sidebar>
</template>
